import type { Node } from "@tiptap/pm/model";
import { TableView } from "@tiptap/pm/tables";
import type { EditorView } from "@tiptap/pm/view";

export const QW_EDITOR_TABLE_CLASS_NAME = "qw-editor-table";

/**
 * Custom NodeView for table nodes that adds the `qw-editor-table` class to
 * the underlying `<table>` element.
 *
 * Why this exists:
 *
 * `prosemirror-tables`' default `TableView` builds the table DOM by hand with
 * `document.createElement("table")` and never propagates `HTMLAttributes` from
 * the schema onto the table element. As a result, in editable mode the table
 * has no class — only the wrapping `<div class="tableWrapper">` carries one.
 * In read-only mode there is no NodeView and TipTap falls back to the schema's
 * `renderHTML`, which *does* apply `HTMLAttributes` — so the table has the
 * class but no wrapper.
 *
 * That asymmetry forced editor CSS to anchor on two different selectors. By
 * adding the class to the table inside the NodeView, both render paths
 * produce a table with the class and the editor CSS can use a single anchor.
 *
 * The wrapper div remains in editable mode (NodeView still injects it for
 * interactive features like column resizing and cell selection) but the
 * stylesheet no longer cares about it for table styling.
 */
export class QwEditorTableView extends TableView {
  constructor(node: Node, defaultCellMinWidth: number, _view: EditorView) {
    super(node, defaultCellMinWidth);
    this.table.classList.add(QW_EDITOR_TABLE_CLASS_NAME);
  }
}
